import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { apiService, setSubscriptionExpiredConfirmedByUserMe, setSubscriptionExpiredHandler } from '../services/apiService';

type SubscriptionRole = 'ADMIN' | 'ADMIN_PM' | 'PROJECT_MANAGER' | 'MEMBER';

interface SubscriptionGuardContextType {
  isExpired: boolean;
  role: SubscriptionRole | null;
  adminEmail: string;
  isLoading: boolean;
  handleProtectedNavigation: (path: string, onAllowed?: () => void) => void;
  openSubscriptionPopup: () => void;
}

const SubscriptionGuardContext = createContext<SubscriptionGuardContextType | undefined>(undefined);

export const useSubscriptionGuard = () => {
  const context = useContext(SubscriptionGuardContext);
  if (!context) throw new Error('useSubscriptionGuard must be used within SubscriptionGuardProvider');
  return context;
};

export const SubscriptionGuardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isExpired, setIsExpired] = useState(false);
  const [role, setRole] = useState<SubscriptionRole | null>(null);
  const [adminEmail, setAdminEmail] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isPopupDismissed, setIsPopupDismissed] = useState(false);
  const canSubscribe = role === 'ADMIN' || role === 'ADMIN_PM';
  const isManageAccountPath = location.pathname === '/manage-account' || location.pathname.startsWith('/manage-account/');
  const isPricingPath = location.pathname === '/pricing' || location.pathname.startsWith('/pricing/');
  const isPaymentPath = location.pathname === '/payment' || location.pathname.startsWith('/payment/');
  const isSubscriptionAllowedPath = isManageAccountPath || isPricingPath || isPaymentPath;
  const isSubscriptionTab = isManageAccountPath && new URLSearchParams(location.search).get('tab') === 'subscription';

  const showManageAccountExpiredPopup = isManageAccountPath && !isSubscriptionTab;

  const openSubscriptionPopup = useCallback(() => {
    if (!isAuthenticated) return;
    if (isManageAccountPath && isSubscriptionTab && canSubscribe) return;
    if (isPopupDismissed && canSubscribe && showManageAccountExpiredPopup) return;
    if ((isPricingPath || isPaymentPath) && canSubscribe) return;
    if (isPopupOpen) return;
    setIsPopupOpen(true);
  }, [isAuthenticated, isPopupDismissed, canSubscribe, isManageAccountPath, isSubscriptionTab, showManageAccountExpiredPopup, isPricingPath, isPaymentPath, isPopupOpen]);

  const closeSubscriptionPopup = useCallback(() => {
    setIsPopupOpen(false);
    setIsPopupDismissed(true);
  }, []);

  const fetchMe = useCallback(async () => {
    if (!isAuthenticated) {
      setSubscriptionExpiredConfirmedByUserMe(false);
      setIsExpired(false);
      setRole(null);
      setAdminEmail('');
      setIsLoading(false);
      return;
    }
    setSubscriptionExpiredConfirmedByUserMe(false);
    try {
      const [meResult, subscriptionResult, subscriptionViewResult] = await Promise.allSettled([
        apiService.get('/user/me'),
        apiService.getSubscription(),
        apiService.getSubscriptionView(),
      ]);

      const meData = meResult.status === 'fulfilled' ? (meResult.value?.data ?? {}) : {};
      const expiredFromMe = Boolean(meData.subscriptionExpired);

      const subscriptionData =
        subscriptionResult.status === 'fulfilled' && !subscriptionResult.value?.error
          ? subscriptionResult.value
          : null;
      const displayPlan = (subscriptionData?.displayPlan || '').toUpperCase();
      const status = (subscriptionData?.status || '').toUpperCase();
      const subStatus = (subscriptionData?.subscriptionStatus || '').toUpperCase();
      const expiredFromSubscription =
        displayPlan === 'EXPIRED' || status === 'INACTIVE' || subStatus === 'EXPIRED';

      const subscriptionViewData =
        subscriptionViewResult.status === 'fulfilled'
          ? subscriptionViewResult.value
          : null;
      const viewStatus = (subscriptionViewData?.status || '').toUpperCase();
      const expiredFromSubscriptionView =
        viewStatus === 'EXPIRED' || viewStatus === 'INACTIVE';

      const expired = expiredFromMe || expiredFromSubscription || expiredFromSubscriptionView;
      setSubscriptionExpiredConfirmedByUserMe(expired);
      setIsExpired(expired);
      setRole((meData.role as SubscriptionRole) ?? null);
      setAdminEmail(typeof meData.adminEmail === 'string' ? meData.adminEmail : '');
    } catch (_error) {
      // Do not infer expired subscription from /user/me errors (transient failures caused false popups on login).
      setSubscriptionExpiredConfirmedByUserMe(false);
      setIsExpired(false);
      setRole(null);
      setAdminEmail('');
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (authLoading) return;
    setIsLoading(true);
    fetchMe();
  }, [authLoading, fetchMe]);

  useEffect(() => {
    const unregister = setSubscriptionExpiredHandler(() => {
      if (!isAuthenticated) return;
      openSubscriptionPopup();
    });
    return unregister;
  }, [isAuthenticated, openSubscriptionPopup]);

  useEffect(() => {
    if (!isExpired) {
      setIsPopupDismissed(false);
      return;
    }
    if (!isSubscriptionAllowedPath) {
      setIsPopupDismissed(false);
    }
  }, [isExpired, isSubscriptionAllowedPath]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!isExpired) return;
    openSubscriptionPopup();
  }, [isAuthenticated, isExpired, openSubscriptionPopup]);

  useEffect(() => {
    if (!isAuthenticated || !isExpired || !canSubscribe || !showManageAccountExpiredPopup) return;
    if (isPopupDismissed) return;
    setIsPopupOpen(true);
  }, [isAuthenticated, isExpired, canSubscribe, showManageAccountExpiredPopup, isPopupDismissed]);

  useEffect(() => {
    if (isSubscriptionTab) setIsPopupOpen(false);
  }, [isSubscriptionTab]);

  const handleProtectedNavigation = useCallback((path: string, onAllowed?: () => void) => {
    if (isExpired) {
      const targetIsManageAccount =
        path === '/manage-account' || path.startsWith('/manage-account/') ||
        path.startsWith('/manage-account?');
      const targetIsSubscriptionAllowed =
        targetIsManageAccount ||
        path === '/pricing' || path.startsWith('/pricing/') ||
        path.startsWith('/pricing?') ||
        path === '/payment' || path.startsWith('/payment/') ||
        path.startsWith('/payment?');
      if (onAllowed) {
        onAllowed();
      } else {
        navigate(path);
      }
      if (!targetIsSubscriptionAllowed) {
        setIsPopupDismissed(false);
        setIsPopupOpen(true);
      } else if (targetIsManageAccount) {
        const targetIsSubscriptionTab = path.includes('tab=subscription');
        if (targetIsSubscriptionTab) {
          setIsPopupOpen(false);
          setIsPopupDismissed(true);
        } else {
          setIsPopupDismissed(false);
          setIsPopupOpen(true);
        }
      } else {
        setIsPopupOpen(false);
        setIsPopupDismissed(true);
      }
      return;
    }
    if (onAllowed) {
      onAllowed();
      return;
    }
    navigate(path);
  }, [isExpired, navigate]);

  const popupMessage = canSubscribe
    ? 'Your plan has expired. Please subscribe to continue.'
    : `Your plan has expired. Please contact your admin: ${adminEmail || 'N/A'}`;

  const value = useMemo(() => ({
    isExpired,
    role,
    adminEmail,
    isLoading,
    handleProtectedNavigation,
    openSubscriptionPopup,
  }), [isExpired, role, adminEmail, isLoading, handleProtectedNavigation, openSubscriptionPopup]);

  if (isLoading) {
    return null;
  }

  return (
    <SubscriptionGuardContext.Provider value={value}>
      {children}
      <Dialog
        open={isPopupOpen}
        onClose={(_, reason) => {
          if (reason === 'backdropClick') return;
          if (!isSubscriptionAllowedPath) return;
        }}
        disableEscapeKeyDown={!isSubscriptionAllowedPath}
        sx={{
          pointerEvents: 'none',
          '& .MuiDialog-container': { pointerEvents: 'none' },
          '& .MuiDialog-paper': { pointerEvents: 'auto' },
        }}
        BackdropProps={{
          sx: {
            left: { xs: 0, sm: '172px' },
            backgroundColor: 'transparent',
            pointerEvents: 'auto',
          },
        }}
      >
        <DialogTitle>Plan Expired</DialogTitle>
        <DialogContent>
          <Typography>{popupMessage}</Typography>
        </DialogContent>
        <DialogActions>
          {canSubscribe && (
            <Button
              variant="contained"
              onClick={() => {
                closeSubscriptionPopup();
                navigate('/manage-account?tab=subscription');
              }}
            >
              Go to Subscription
            </Button>
          )}
          {showManageAccountExpiredPopup && <Button onClick={closeSubscriptionPopup}>Close</Button>}
        </DialogActions>
      </Dialog>
    </SubscriptionGuardContext.Provider>
  );
};
